import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRequest } from '@/hooks';
import { getAccessToken } from '@/auth';

import { useConnectAuthCode } from './hooks/useConnectAuthCode';
import { useCountdown } from './hooks/useCountdown';
import { useExchangeAuthCode } from './hooks/useExchangeAuthCode';
import { StoredExchangeResult, useExchangeCache } from './hooks/useExchangeCache';
import { resolveConnectStatus } from './resolveStatus';
import ErrorState from './states/ErrorState';
import ExpiredState from './states/ExpiredState';
import LoadingState from './states/LoadingState';
import NoChannelState from './states/NoChannelState';
import ReadyState from './states/ReadyState';
import UnauthenticatedState from './states/UnauthenticatedState';
import { ChannelOption } from './types';
import { buildSchoolCallbackUrl, isValidAuthCode } from './utils';

interface ConnectControllerProps {
  authCode: string | null;
  /**
   * 캐시된 exchange 결과 (Resume 흐름).
   * 주어지면 exchange API 호출을 건너뛰고 곧바로 success 상태로 시작한다.
   * authCode 와 일치할 때만 사용된다 — 일치하지 않으면 일반 흐름 (재호출).
   */
  cachedExchange?: StoredExchangeResult | null;
}

const FALLBACK_TTL_MS = 5 * 60 * 1000;

/**
 * authCode가 정해진 시점부터 6 상태 흐름을 관리한다.
 *
 * 흐름:
 *   authCode 없음/형식 invalid     → error (즉시)
 *   exchange 진행 중               → loading
 *   exchange 410/expired          → expired
 *   exchange 기타 실패             → error
 *   exchange 성공
 *     ├─ 미로그인                   → success-unauthenticated
 *     ├─ 로그인 + 채널 0개         → success-no-channel
 *     └─ 로그인 + 채널 1개 이상    → success-ready
 */
export default function ConnectController({
  authCode,
  cachedExchange = null
}: ConnectControllerProps) {
  // authCode 형식 검증 — 유효하지 않으면 호출조차 하지 않는다.
  const isCodeValid = isValidAuthCode(authCode);

  // 캐시 hit 조건: authCode 일치 + 만료 전. (Resume에서 이미 만료 체크된 캐시를 전달받음.)
  const seededData = useMemo(
    () =>
      cachedExchange && cachedExchange.authCode === authCode && isCodeValid
        ? cachedExchange.data
        : null,
    [cachedExchange, authCode, isCodeValid]
  );

  const exchange = useExchangeAuthCode(isCodeValid ? authCode : null, { seededData });

  // 캐시 저장 (첫 exchange 성공 시).
  const { save: saveExchangeCache, clear: clearExchangeCache } = useExchangeCache();

  // 로그인 여부 동기 확인. localStorage 기반.
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken();
        if (!cancelled) setIsAuthenticated(Boolean(token));
      } catch {
        if (!cancelled) setIsAuthenticated(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 로그인 상태일 때만 채널 리스트를 조회한다 (미로그인 시 401 방지).
  const { data: appsResponse } = useRequest<CommonResponseModel<ApplicationResponseModel[]>>(
    isAuthenticated ? '/apps' : null,
    {},
    {
      headers: { 'x-rwd-api-version': '1.1' }
    }
  );
  const apps = appsResponse?.data;

  // 본인 채널 식별을 위해 /api/me 조회 (로그인 후에만)
  const { data: me } = useRequest<MyInfo>(isAuthenticated ? '/api/me' : null);

  // user 본인이 owner 또는 author인 채널만 노출.
  // me 정보가 아직 안 왔으면 전체를 노출 (보수적).
  const channels: ChannelOption[] = useMemo(() => {
    if (!apps) return [];
    return apps
      .filter((app) => {
        if (!app.id) return false;
        if (!me || !me.id) return true;
        const member = app.members?.find((m) => m.id === me.id);
        return member?.level === 'Owner' || member?.level === 'Author';
      })
      .map((app) => ({
        id: app.id ?? '',
        name: app.name ?? '',
        description: app.description ?? null,
        imageUrl: app.imageUrl ?? null
      }));
  }, [apps, me]);

  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const handleSelectChannel = useCallback((id: string) => setSelectedChannelId(id), []);

  // 사용자가 단일 채널인 경우에도 selector를 보여주지만, 디폴트로 해당 채널을 미리 선택해 둔다.
  useEffect(() => {
    if (channels.length === 1 && selectedChannelId === null) {
      setSelectedChannelId(channels[0].id);
    }
  }, [channels, selectedChannelId]);

  // sessionStorage에 보관 (미로그인 → 로그인 / 채널생성 흐름 대비)
  const { save, clear } = useConnectAuthCode();
  // exchange가 성공할 때, 남은 시간(만료까지)을 추정해 보관한다.
  // 정확한 만료시점은 서버 응답에 없음 — UI 카운트다운은 보수적으로 5분(300s)을 기준으로 잡는다.
  // 향후 BE에서 expires_at을 응답에 포함하면 그 값으로 교체한다.
  const [expiresAt, setExpiresAt] = useState<number | null>(null);

  useEffect(() => {
    if (exchange.status === 'success' && isCodeValid && authCode && exchange.data) {
      // 캐시 hit 으로 시작한 경우엔 그 만료시점을 그대로 사용, 아니면 신규 만료시점을 만든다.
      const cachedExpiresAt =
        cachedExchange && cachedExchange.authCode === authCode ? cachedExchange.expiresAt : null;
      const at = cachedExpiresAt ?? Date.now() + FALLBACK_TTL_MS;

      setExpiresAt(at);
      save(authCode, at);

      // 첫 exchange 성공 시 캐시 저장. (캐시 hit 인 경우엔 동일 값으로 덮어써도 무해.)
      saveExchangeCache({
        authCode,
        data: exchange.data,
        expiresAt: at
      });
    }
    // exchange 실패/만료시에는 보관소를 비운다 (재시도 link 받게).
    if (exchange.status === 'expired' || exchange.status === 'error') {
      clear();
      clearExchangeCache();
      setExpiresAt(null);
    }
  }, [
    exchange.status,
    exchange.data,
    isCodeValid,
    authCode,
    cachedExchange,
    save,
    clear,
    saveExchangeCache,
    clearExchangeCache
  ]);

  // 카운트다운: 0초 도달 시 expired 상태로 전이.
  const [didLocalExpire, setDidLocalExpire] = useState(false);
  const handleExpire = useCallback(() => {
    setDidLocalExpire(true);
    clear();
    clearExchangeCache();
  }, [clear, clearExchangeCache]);
  const remainingSeconds = useCountdown(
    exchange.status === 'success' ? expiresAt : null,
    handleExpire
  );

  // 액션 핸들러
  const handleLogin = useCallback(() => {
    if (typeof window === 'undefined') return;
    const redirect = encodeURIComponent('/connect/resume');
    window.location.assign(`/auth/login?redirect=${redirect}`);
  }, []);

  const handleCreateChannel = useCallback(() => {
    if (typeof window === 'undefined') return;
    const returnPath = encodeURIComponent('/connect/resume');
    window.location.assign(`/createApp?return=${returnPath}`);
  }, []);

  const handleConnect = useCallback(() => {
    if (!selectedChannelId || !authCode) return;
    const callbackUrl = buildSchoolCallbackUrl({
      channelId: selectedChannelId,
      authCode
    });
    // 사용 후엔 보관소를 비운다.
    clear();
    clearExchangeCache();
    window.location.assign(callbackUrl);
  }, [selectedChannelId, authCode, clear, clearExchangeCache]);

  const handleSignupSuccess = useCallback(() => {
    setIsAuthenticated(true);
  }, []);

  // 6 상태 결정 (순수 함수에 위임 — 테스트 가능)
  const status = useMemo(
    () =>
      resolveConnectStatus({
        authCode,
        isAuthCodeValid: isCodeValid,
        didLocalExpire,
        exchangeStatus: exchange.status,
        isAuthenticated,
        hasFetchedApps: Boolean(apps),
        ownedChannelCount: channels.length
      }),
    [authCode, isCodeValid, didLocalExpire, exchange.status, isAuthenticated, apps, channels.length]
  );

  // 렌더 분기
  switch (status) {
    case 'loading':
      return <LoadingState />;

    case 'expired':
      return <ExpiredState />;

    case 'error': {
      // exchange 에러 메시지가 있으면 그것을, 아니면 authCode 자체가 없는 케이스로 간주.
      const message =
        exchange.errorMessage ??
        (authCode ? '유효하지 않은 연동 링크' : '연동 정보가 없어요. Realworld School에서 다시 시작해 주세요.');
      // authCode가 없을 땐 새로고침 의미가 없으므로 retry 버튼을 숨긴다.
      const onRetry = authCode ? () => window.location.reload() : undefined;
      return <ErrorState message={message} onRetry={onRetry} />;
    }

    case 'success-unauthenticated':
      return (
        <UnauthenticatedState
          authCode={authCode ?? ''}
          email={exchange.data?.email ?? ''}
          nickname={exchange.data?.nickname ?? ''}
          remainingSeconds={remainingSeconds}
          onLogin={handleLogin}
          onSignupSuccess={handleSignupSuccess}
        />
      );

    case 'success-no-channel':
      return (
        <NoChannelState
          email={exchange.data?.email ?? ''}
          nickname={exchange.data?.nickname ?? ''}
          remainingSeconds={remainingSeconds}
          onCreateChannel={handleCreateChannel}
        />
      );

    case 'success-ready':
      return (
        <ReadyState
          email={exchange.data?.email ?? ''}
          nickname={exchange.data?.nickname ?? ''}
          remainingSeconds={remainingSeconds}
          channels={channels}
          selectedChannelId={selectedChannelId}
          onSelectChannel={handleSelectChannel}
          onConnect={handleConnect}
        />
      );

    default:
      return <LoadingState />;
  }
}
